import { computed, type MaybeRef, unref } from 'vue';
import { useLanguage } from './useLanguage';
import type { Discount } from '@/models/Discounts';
import {
	getBillingOccurenceDescription, getISOCurrency, getLocalizedDisplayName, getRecurringFee, getRecurringFeeInCents, type PlanVariant,
} from '@/models/PlanVariant';
import type { ISOCurrency } from '@/models/Currency';

type PlanVariantOptions = {
	planVariant?: MaybeRef<PlanVariant | undefined>;
	discount?: MaybeRef<Discount | null>;
};

export const usePlanVariant = (options: PlanVariantOptions) => {
	const planVariant = computed(() => unref(options.planVariant));
	const discount = computed(() => unref(options.discount));

	const recurringFee = computed(() => {
		if (!planVariant.value) {
			return 0;
		}

		return getRecurringFee(planVariant.value);
	});

	const recurringFeeInCents = computed(() => {
		if (!planVariant.value) {
			return 0;
		}

		return getRecurringFeeInCents(planVariant.value);
	});

	const hasDiscountApplied = computed(() => !!(discount.value && unref(discount)));

	const planCategory = computed(() => {
		if (!planVariant.value) {
			return undefined;
		}

		return planVariant.value.CustomFields.category;
	});

	const billingOccurence = computed(() => {
		if (!planVariant.value) {
			return undefined;
		}

		return getBillingOccurenceDescription(planVariant.value);
	});

	const currency = computed<ISOCurrency>(() => {
		if (!planVariant.value) {
			return 'EUR';
		}

		return getISOCurrency(planVariant.value);
	});

	const pricePerOccurenceInCents = computed(() => recurringFeeInCents.value);

	const pricePerOccurenceInCentsWithMaybeDiscount = computed(() => {
		if (discount.value) {
			return recurringFeeInCents.value * (1 - (discount.value.Effect.ReductionPercent / 100));
		}

		return recurringFeeInCents.value;
	});

	const pricePerMonthInCents = computed(() => {
		if (!planVariant.value) {
			return 0;
		}

		if (!planVariant.value.BillingPeriod.Quantity) {
			return 0;
		}

		if (!billingOccurence.value) {
			return 0;
		}

		return pricePerOccurenceInCentsWithMaybeDiscount.value / billingOccurence.value.months;
	});

	const { selectedLanguage } = useLanguage();

	const localizedDisplayName = computed(() => {
		if (!planVariant.value) {
			return undefined;
		}

		return getLocalizedDisplayName(planVariant.value, selectedLanguage.value.code);
	});

	return {
		currency,
		recurringFee,
		recurringFeeInCents,
		hasDiscountApplied,
		planCategory,
		billingOccurence,
		pricePerOccurenceInCents,
		pricePerOccurenceInCentsWithMaybeDiscount,
		pricePerMonthInCents,
		localizedDisplayName,
	};
};
